<template>
  <div class="users-page">
    <CreateAdminModal
      @adminCreated="onAdminCreated"
      v-if="creatingUser"
      @closed="creatingUser = false"
    />

    <!-- Header -->
    <div class="users-header">
      <h1 class="users-title">{{ $t("message.users_page.title") }}</h1>
      <button class="btn-dark-button" @click="creatingUser = true">
        + {{ $t("message.users_page.create_new_admin") }}
      </button>
    </div>

    <!-- Tabs -->
    <div class="users-tabs">
      <button
        class="users-tab"
        :class="{ 'users-tab--active': activeTab === 'admins' }"
        @click="activeTab = 'admins'"
      >
        Admins
      </button>
      <button
        class="users-tab"
        :class="{ 'users-tab--active': activeTab === 'sellers' }"
        @click="activeTab = 'sellers'"
      >
        Infoauto Sellers
      </button>
    </div>

    <!-- ── Admins tab ─────────────────────────────────── -->
    <template v-if="activeTab === 'admins'">
    <!-- Filters bar -->
    <div class="users-filters">
      <input
        class="filter-input"
        type="text"
        :placeholder="$t('message.users_page.filter_users')"
        v-model="search"
      />
      <select class="filter-select" v-model="searcStore">
        <option value="">{{ $t("message.users_page.filter_by_store") }}</option>
        <option v-for="store in stores" :value="store.code" :key="store.code">
          {{ store.name }}
        </option>
      </select>
      <label class="inactive-toggle">
        <input type="checkbox" v-model="showInactive" />
        <span>Show inactive</span>
      </label>
      <span class="user-count">{{ filteredUsers.length }} users</span>
    </div>

    <!-- Main layout: list + detail panel -->
    <div class="users-layout" :class="{ 'users-layout--split': selectedUser }">
      <!-- User list -->
      <div class="users-list-wrap">
        <div v-if="loading" class="flex justify-center py-12">
          <BaseSmallSpinner />
        </div>
        <div v-else class="users-list">
          <div
            v-for="user in filteredUsers"
            :key="user.id"
            class="user-row"
            :class="{
              'user-row--active': selectedUser && selectedUser.id === user.id,
              'user-row--inactive': user.enabled === '0',
              'user-row--locked': user.invalidLoginAttempts >= 8
            }"
            @click="selectUser(user)"
          >
            <div
              class="user-row__avatar"
              :style="{ background: avatarColor(user.name) }"
            >
              {{ initials(user.name) }}
            </div>
            <div class="user-row__info">
              <div class="user-row__name">{{ user.name }}</div>
              <div class="user-row__email">{{ user.email }}</div>
            </div>
            <div class="user-row__meta">
              <span class="role-badge">{{ user.role }}</span>
              <span
                v-if="user.enabled === '0'"
                class="status-badge status-badge--inactive"
                >Inactive</span
              >
              <span
                v-else-if="user.invalidLoginAttempts >= 8"
                class="status-badge status-badge--locked"
                >Locked</span
              >
              <span v-else class="status-badge status-badge--active"
                >Active</span
              >
            </div>
          </div>
          <div v-if="!filteredUsers.length" class="empty-state">
            No users match your filters
          </div>
        </div>
      </div>

      <!-- Detail panel -->
      <transition name="panel-slide">
        <div v-if="selectedUser" class="user-detail-panel">
          <button class="panel-close" @click="selectedUser = null">✕</button>

          <div
            class="panel-avatar"
            :style="{ background: avatarColor(selectedUser.name) }"
          >
            {{ initials(selectedUser.name) }}
          </div>
          <h2 class="panel-name">{{ selectedUser.name }}</h2>
          <p class="panel-email">{{ selectedUser.email }}</p>

          <div class="panel-badges">
            <span class="role-badge role-badge--large">{{
              selectedUser.role
            }}</span>
            <span
              v-if="selectedUser.enabled === '0'"
              class="status-badge status-badge--inactive"
              >Inactive</span
            >
            <span
              v-else-if="selectedUser.invalidLoginAttempts >= 8"
              class="status-badge status-badge--locked"
              >Locked</span
            >
            <span v-else class="status-badge status-badge--active">Active</span>
          </div>

          <router-link
            :to="{ name: 'UserPage', params: { userId: selectedUser.id } }"
            class="panel-full-link"
          >
            View full profile →
          </router-link>

          <div class="panel-detail-rows">
            <div class="panel-detail-row">
              <span class="panel-detail-label">Stores</span>
              <span class="panel-detail-value">{{
                selectedUser.stores || "—"
              }}</span>
            </div>
            <div
              class="panel-detail-row"
              v-if="selectedUser.invalidLoginAttempts"
            >
              <span class="panel-detail-label">Failed logins</span>
              <span
                class="panel-detail-value"
                :class="{
                  'text-red-500': selectedUser.invalidLoginAttempts >= 8
                }"
              >
                {{ selectedUser.invalidLoginAttempts }}
              </span>
            </div>
          </div>
        </div>
      </transition>
    </div>
    </template>

    <!-- ── Infoauto Sellers tab ───────────────────────── -->
    <template v-if="activeTab === 'sellers'">
      <div class="users-filters">
        <span class="active-store-label">
          Store: <b>{{ (activeStore && activeStore.name) || "—" }}</b>
        </span>
        <input
          class="filter-input"
          type="text"
          placeholder="Filter sellers"
          v-model="sellersSearch"
        />
        <label class="inactive-toggle">
          <input type="checkbox" v-model="sellersShowDisabled" />
          <span>Show disabled</span>
        </label>
        <button class="action-btn" @click="loadSellers" :disabled="sellersLoading">
          ↻ Refresh
        </button>
        <span class="user-count">{{ filteredSellers.length }} sellers</span>
      </div>

      <div class="sellers-sync">
        <SyncGeneric
          title="Sync sellers"
          type="sellers"
          cta="Sync sellers"
        ></SyncGeneric>
      </div>

      <div class="sellers-wrap">
        <div v-if="sellersLoading" class="flex justify-center py-12">
          <BaseSmallSpinner />
        </div>
        <div v-else-if="sellersError" class="empty-state">
          Could not load sellers from this store
        </div>
        <table v-else class="sellers-table">
          <thead>
            <tr>
              <th
                v-for="col in sellerColumns"
                :key="col.key"
                class="sortable-th"
                @click="setSellerSort(col.key)"
              >
                {{ col.label }}
                <span class="sort-indicator">
                  <template v-if="sellerSortKey === col.key">{{
                    sellerSortDir === "asc" ? "▲" : "▼"
                  }}</template>
                  <template v-else>⇅</template>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="seller in filteredSellers"
              :key="seller.id"
              :class="{ 'seller-row--no-admin': !seller.adminExists }"
            >
              <td>{{ seller.nombreVendedor || "—" }}</td>
              <td>{{ seller.email || "—" }}</td>
              <td>{{ seller.vendedorId || "—" }}</td>
              <td>{{ seller.nombreDpto || "—" }}</td>
              <td>
                <span
                  v-if="seller.adminExists && seller.adminEnabled"
                  class="status-badge status-badge--active"
                  >✓ {{ seller.adminName || "linked" }}</span
                >
                <span
                  v-else-if="seller.adminExists"
                  class="status-badge status-badge--locked"
                  >Admin disabled</span
                >
                <span
                  v-else
                  class="status-badge status-badge--inactive"
                  >Not in admin_v2</span
                >
              </td>
              <td>
                <span v-if="seller.defaultSeller" class="role-badge">default</span>
                <span v-else class="text-gray-400">—</span>
              </td>
              <td>
                <BaseSwitchCustom
                  :value="!!seller.enabled"
                  :disabled="seller.savingEnabled"
                  @input="toggleSellerEnabled(seller, $event)"
                />
              </td>
            </tr>
            <tr v-if="!filteredSellers.length">
              <td colspan="7" class="empty-state">No sellers found</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script>
import CreateAdminModal from "@/components/CreateAdminModal.vue";
import SyncGeneric from "@/components/Infoauto/SyncGeneric.vue";
import { getAdminUsers } from "@/api/masterAdmin/AdminUsersService.js";
import {
  getAllInfoautoSellers,
  updateInfoautoSeller
} from "@/api/masterAdmin/InfoautoSellersService.js";

const AVATAR_COLORS = [
  "#6366f1",
  "#8b5cf6",
  "#ec4899",
  "#14b8a6",
  "#f59e0b",
  "#3b82f6",
  "#10b981",
  "#f97316"
];

export default {
  name: "UsersPage",
  components: { CreateAdminModal, SyncGeneric },
  data() {
    return {
      users: [],
      loading: true,
      search: "",
      searcStore: "",
      showInactive: false,
      creatingUser: false,
      selectedUser: null,
      activeTab: "admins",
      sellers: [],
      sellersLoading: false,
      sellersError: false,
      sellersSearch: "",
      sellersShowDisabled: false,
      sellerSortKey: "nombreVendedor",
      sellerSortDir: "asc",
      sellerColumns: [
        { key: "nombreVendedor", label: "Name" },
        { key: "email", label: "Email" },
        { key: "vendedorId", label: "Vendedor ID" },
        { key: "nombreDpto", label: "Department" },
        { key: "adminStatus", label: "Admin" },
        { key: "defaultSeller", label: "Default" },
        { key: "enabled", label: "Enabled" }
      ]
    };
  },
  computed: {
    stores() {
      return this.$store.getters["general/getStores"];
    },
    filteredUsers() {
      return this.users.filter(user => {
        if (!this.showInactive && user.enabled === "0") return false;
        if (
          this.searcStore &&
          !user.stores.split(",").includes(this.searcStore)
        )
          return false;
        const q = this.search.toLowerCase();
        return (
          !q ||
          user.name.toLowerCase().includes(q) ||
          user.email.toLowerCase().includes(q)
        );
      });
    },
    filteredSellers() {
      const q = this.sellersSearch.toLowerCase();
      const filtered = this.sellers.filter(s => {
        if (!this.sellersShowDisabled && !s.enabled) return false;
        if (!q) return true;
        return (
          (s.nombreVendedor || "").toLowerCase().includes(q) ||
          (s.email || "").toLowerCase().includes(q) ||
          (s.vendedorId || "").toLowerCase().includes(q)
        );
      });
      const key = this.sellerSortKey;
      const dir = this.sellerSortDir === "asc" ? 1 : -1;
      const valOf = s => {
        if (key === "adminStatus") {
          if (s.adminExists && s.adminEnabled) return 2;
          if (s.adminExists) return 1;
          return 0;
        }
        if (key === "enabled" || key === "defaultSeller") {
          return s[key] ? 1 : 0;
        }
        return (s[key] ?? "").toString().toLowerCase();
      };
      return filtered.slice().sort((a, b) => {
        const av = valOf(a);
        const bv = valOf(b);
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return 0;
      });
    },
    activeStore() {
      return this.$store.getters["general/getActiveStore"];
    }
  },
  mounted() {
    this.getUsers();
  },
  watch: {
    activeTab(tab) {
      if (tab === "sellers" && !this.sellers.length) this.loadSellers();
    }
  },
  methods: {
    getUsers() {
      this.loading = true;
      getAdminUsers()
        .then(response => {
          this.users = response;
        })
        .catch(console.error)
        .finally(() => {
          this.loading = false;
        });
    },
    selectUser(user) {
      this.selectedUser = this.selectedUser?.id === user.id ? null : user;
    },
    onAdminCreated() {
      this.creatingUser = false;
      this.getUsers();
    },
    initials(name) {
      if (!name) return "?";
      return name
        .split(" ")
        .map(w => w[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();
    },
    avatarColor(name) {
      if (!name) return AVATAR_COLORS[0];
      const i = name.charCodeAt(0) % AVATAR_COLORS.length;
      return AVATAR_COLORS[i];
    },
    setSellerSort(key) {
      if (this.sellerSortKey === key) {
        this.sellerSortDir = this.sellerSortDir === "asc" ? "desc" : "asc";
      } else {
        this.sellerSortKey = key;
        this.sellerSortDir = "asc";
      }
    },
    async loadSellers() {
      this.sellersLoading = true;
      this.sellersError = false;
      const { data, error } = await getAllInfoautoSellers();
      this.sellers = (data || []).map(s => ({ ...s, savingEnabled: false }));
      this.sellersError = error;
      this.sellersLoading = false;
    },
    async toggleSellerEnabled(seller, value) {
      this.$set(seller, "savingEnabled", true);
      try {
        await updateInfoautoSeller(seller.id, { enabled: value });
        this.$set(seller, "enabled", value);
        this.$toast.open({
          message: value ? "Seller enabled" : "Seller disabled",
          type: "success",
          position: "bottom-right"
        });
      } catch (e) {
        this.$toast.open({
          message: e.response?.data || "Error updating seller",
          type: "error",
          position: "bottom-right"
        });
      } finally {
        this.$set(seller, "savingEnabled", false);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.users-page {
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  height: 100%;
}

/* Header */
.users-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}
.users-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
}

/* Filters */
.users-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.625rem;
  padding: 0.75rem 1rem;
}
.filter-input {
  flex: 1;
  min-width: 180px;
  padding: 0.4rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  &:focus {
    outline: none;
    border-color: #6366f1;
  }
}
.filter-select {
  padding: 0.4rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background: #fff;
  &:focus {
    outline: none;
    border-color: #6366f1;
  }
}
.inactive-toggle {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.825rem;
  color: #6b7280;
  cursor: pointer;
  user-select: none;
  input {
    cursor: pointer;
  }
}
.user-count {
  font-size: 0.8rem;
  color: #9ca3af;
  margin-left: auto;
}

/* Layout */
.users-layout {
  display: flex;
  gap: 1rem;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  &--split .users-list-wrap {
    flex: 1;
  }
}
.users-list-wrap {
  flex: 1;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
}

/* User rows */
.users-list {
  display: flex;
  flex-direction: column;
}
.user-row {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #f3f4f6;
  cursor: pointer;
  transition: background 0.1s;
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: #f9fafb;
  }
  &--active {
    background: #eef2ff !important;
  }
  &--inactive {
    opacity: 0.6;
  }
  &--locked {
    background: #fef2f2;
    &:hover {
      background: #fee2e2;
    }
  }
}
.user-row__avatar {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
}
.user-row__info {
  flex: 1;
  min-width: 0;
}
.user-row__name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.user-row__email {
  font-size: 0.775rem;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.user-row__meta {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-shrink: 0;
}

/* Badges */
.role-badge {
  font-size: 0.7rem;
  font-weight: 600;
  background: #e0e7ff;
  color: #4338ca;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  &--large {
    font-size: 0.8rem;
    padding: 0.25rem 0.75rem;
  }
}
.status-badge {
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  &--active {
    background: #dcfce7;
    color: #15803d;
  }
  &--inactive {
    background: #f3f4f6;
    color: #6b7280;
  }
  &--locked {
    background: #fef2f2;
    color: #dc2626;
  }
}

/* Detail panel */
.user-detail-panel {
  width: 280px;
  flex-shrink: 0;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 1.5rem 1.25rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  position: relative;
  overflow-y: auto;
}
.panel-close {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  background: none;
  border: none;
  font-size: 0.875rem;
  color: #9ca3af;
  cursor: pointer;
  &:hover {
    color: #374151;
  }
}
.panel-avatar {
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 0.25rem;
}
.panel-name {
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
  text-align: center;
}
.panel-email {
  font-size: 0.8rem;
  color: #6b7280;
  margin: 0;
  text-align: center;
  word-break: break-all;
}
.panel-badges {
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 0.25rem;
}
.panel-detail-rows {
  width: 100%;
  border-top: 1px solid #f3f4f6;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.panel-detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.8rem;
}
.panel-detail-label {
  color: #9ca3af;
}
.panel-detail-value {
  font-weight: 600;
  color: #374151;
  text-align: right;
  word-break: break-all;
}
.panel-full-link {
  font-size: 0.825rem;
  font-weight: 600;
  color: #6366f1;
  text-decoration: none;
  padding: 0.4rem 1rem;
  border: 1.5px solid #6366f1;
  border-radius: 999px;
  transition: background 0.12s;
  &:hover {
    background: #eef2ff;
  }
}

/* Empty state */
.empty-state {
  padding: 3rem;
  text-align: center;
  color: #9ca3af;
  font-size: 0.875rem;
}

/* Tabs */
.users-tabs {
  display: flex;
  gap: 0.25rem;
  border-bottom: 1px solid #e5e7eb;
}
.users-tab {
  padding: 0.5rem 1rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #6b7280;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  &:hover {
    color: #374151;
  }
  &--active {
    color: #4338ca;
    border-bottom-color: #6366f1;
  }
}

/* Sellers table */
.sellers-wrap {
  flex: 1;
  overflow: auto;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
}
.sellers-sync {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.625rem;
  padding: 0.75rem 1rem;
}
.sortable-th {
  cursor: pointer;
  user-select: none;
  &:hover {
    background: #f3f4f6 !important;
  }
}
.sort-indicator {
  margin-left: 0.25rem;
  font-size: 0.65rem;
  color: #9ca3af;
}
.sellers-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.825rem;
  th,
  td {
    text-align: left;
    padding: 0.625rem 0.875rem;
    border-bottom: 1px solid #f3f4f6;
  }
  th {
    background: #f9fafb;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    font-size: 0.7rem;
    letter-spacing: 0.04em;
  }
  tbody tr:hover {
    background: #f9fafb;
  }
  tbody tr.seller-row--no-admin {
    background: #fef2f2;
    &:hover {
      background: #fee2e2;
    }
  }
}

/* Panel slide transition */
.panel-slide-enter-active,
.panel-slide-leave-active {
  transition: transform 0.2s ease, opacity 0.2s ease;
}
.panel-slide-enter,
.panel-slide-leave-to {
  transform: translateX(20px);
  opacity: 0;
}
</style>
